import {
  Link,
  Button,
  Stack,
  SelectChangeEvent,
  Card,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  IconButton,
  TableSortLabel,
  Box,
  useTheme,
} from '@mui/material';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import { useGetAllUnitsInBuilding } from '@/api/endpoints/unit/useGetAllUnitsInBuilding';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { getResolvedRoutePath } from '@/utils/router';

import UnitCreateModal from '@/components/unit/UnitCreateModal';

type UnitSortableField = 'name' | 'tenants';

const TABLE_HEAD: {
  id: UnitSortableField;
  label: string;
  sortable: boolean;
  width: string;
}[] = [
  { id: 'name', label: 'Unit', sortable: true, width: '50%' },
  { id: 'tenants', label: 'Tenants', sortable: true, width: '50%' },
];

type Order = 'asc' | 'desc';

const BuildingViewUnitListSubpage: React.FC = () => {
  const { session } = useAuth();
  const { id } = useParams<ExtractParams<PMPRoutes.buildingViewUnitList>>();
  const location = useLocation();
  const theme = useTheme();
  const [isUnitCreateModalOpen, setIsUnitCreateModalOpen] = useState(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<UnitSortableField>('name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleRequestSort = (property: UnitSortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(e.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const getAllUnitsQuery = useGetAllUnitsInBuilding(
    { buildingId: Number(id) },
    !!session?.access_token
  );

  const filteredData = useMemo(() => {
    let data = getAllUnitsQuery.data ?? [];

    if (filterName) {
      data = data.filter(
        unit =>
          unit.name?.toLowerCase().includes(filterName.toLowerCase()) ||
          unit.tenants.some(t => t.full_name.toLowerCase().includes(filterName.toLowerCase()))
      );
    }

    return [...data].sort((a, b) => {
      const aVal = orderBy === 'name' ? a.name : a.tenants.map(t => t.full_name).join(', ');
      const bVal = orderBy === 'name' ? b.name : b.tenants.map(t => t.full_name).join(', ');
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [getAllUnitsQuery.data, filterName, order, orderBy]);

  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const onAddUnitClick = useCallback(() => {
    setIsUnitCreateModalOpen(true);
  }, []);

  // const BuildingViewUnitListNoRowsOverlay = useCallback(
  //   () => <NoRowsOverlay entityName="Unit" onAddEntityClick={onAddUnitClick} />,
  //   [onAddUnitClick]
  // );

  // Auto-open unit create modal if URL has openAddUnit=true
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openAddUnit') === 'true') {
      setIsUnitCreateModalOpen(true);
    }
  }, [location.search]);

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative', pt: { sm: 7 } }}>
      <Button
        color="primary"
        variant="contained"
        size="medium"
        startIcon={<PlusIcon />}
        onClick={onAddUnitClick}
        sx={{
          position: { sm: 'absolute' },
          top: { sm: -2 },
          right: { sm: 0 },
        }}
      >
        Add Unit
      </Button>

      <Card>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search units or tenants..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon />
              </InputAdornment>
            }
            sx={{ maxWidth: 320, bgcolor: 'common.white' }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(head => (
                  <TableCell
                    key={head.id}
                    align="left"
                    sortDirection={orderBy === head.id ? order : false}
                    sx={{
                      width: head.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {head.sortable ? (
                      <TableSortLabel
                        active={orderBy === head.id}
                        direction={orderBy === head.id ? order : 'asc'}
                        onClick={() => handleRequestSort(head.id)}
                      >
                        {head.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {head.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllUnitsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {!getAllUnitsQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center">
                    No units found
                  </TableCell>
                </TableRow>
              )}
              {!getAllUnitsQuery.isLoading &&
                displayedData.map(unit => (
                  <TableRow
                    key={String(unit.id)}
                    sx={{
                      bgcolor: theme.palette.secondary.contrastText,
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell>
                      <Link
                        href={getResolvedRoutePath(PMPRoutes.unitView, { id: String(unit.id) })}
                        sx={{
                          color: 'text.primary',
                          textDecoration: 'none',
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {unit.name}
                      </Link>
                    </TableCell>
                    <TableCell>{unit.tenants.map(t => t.full_name).join(', ')}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isUnitCreateModalOpen && (
        <UnitCreateModal buildingId={id} onClose={() => setIsUnitCreateModalOpen(false)} />
      )}
    </Stack>
  );
};

export default BuildingViewUnitListSubpage;
